"use client";

import React, { useState, useRef } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
const experiences = [{
  id: 1,
  src: "https://prime-rhino.10web.cloud/wp-content/uploads/2026/05/Shortened-Lets-Groove.mp4",
  venue: "The Grand Ballroom",
  city: "Los Angeles",
  aspect: "aspect-[9/16]",
  srcClassName: "from-background/90"
}, {
  id: 2,
  src: "https://prime-rhino.10web.cloud/wp-content/uploads/2026/05/Short-Breezing-take-2.mp4",
  venue: "Sunset Lounge",
  city: "Santa Monica",
  aspect: "aspect-video",
  srcClassName: "from-background/80"
}, {
  id: 3,
  src: "https://prime-rhino.10web.cloud/wp-content/uploads/2026/05/jazz-30-sec-no-vocals.mp4",
  venue: "Crystal Plaza",
  city: "Beverly Hills",
  aspect: "aspect-video",
  srcClassName: "from-background/80"
}, {
  id: 4,
  src: "https://prime-rhino.10web.cloud/wp-content/uploads/2026/05/Short-clib-before-i-let-go-1.mp4",
  venue: "The Jazz Cellar",
  city: "Pasadena",
  aspect: "aspect-[9/16]",
  srcClassName: "from-background/90"
}];
export default function TheFanExperience() {
  const [playingId, setPlayingId] = useState<number | null>(null);
  const videoRefs = useRef<{
    [key: number]: HTMLVideoElement | null;
  }>({});
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });

  // FIX 1: Removed the `loadedVideos` gate — no longer blocks playback
  // FIX 2: Removed `crossOrigin="anonymous"` to avoid CORS-related load failures
  const handlePlayVideo = (id: number) => {
    const video = videoRefs.current[id];
    if (!video) return;

    // Pause any currently playing video
    if (playingId !== null && playingId !== id) {
      const prevVideo = videoRefs.current[playingId];
      if (prevVideo) {
        prevVideo.pause();
        prevVideo.muted = true;
      }
    }
    setPlayingId(id);
    video.muted = false;
    video.play().catch(err => {
      console.error("Playback failed:", err);
      // Fallback: retry muted (autoplay policy may require it)
      video.muted = true;
      video.play().catch(console.error);
    });
  };
  const handleVideoPause = (id: number) => {
    setPlayingId(prev => prev === id ? null : prev);
  };
  return <section data-section-id="122626" id="live-at-the-center-stage" className="dark bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className={`mb-16 md:mb-24 text-center transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif font-light mb-6 uppercase tracking-tighter text-foreground italic">
            Live at the Center Stage
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg md:text-xl font-sans leading-relaxed">
            Experience the soulful resonance of Synergy Sound. A cinematic journey through our live
            performances across Southern California.
          </p>
        </div>

        {/* Video Grid */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
          {experiences.map((item, i) => <div key={item.id} data-index={i} className="group relative overflow-hidden rounded-sm break-inside-avoid bg-card shadow-2xl transition-all duration-500">
              <div className={`relative w-full ${item.aspect} overflow-hidden`}>

                {/* FIX 2: Removed crossOrigin="anonymous" */}
                <video ref={el => {
              videoRefs.current[item.id] = el;
            }} src={item.src} loop muted playsInline onPause={() => handleVideoPause(item.id)} onError={() => {
              console.error(`Failed to load video for ${item.venue}`);
            }} aria-label={`Video of Synergy Sound performing at ${item.venue} in ${item.city}`} className="w-full h-full object-cover transition-transform duration-1000 ease-out grayscale group-hover:grayscale-0">
                  Your browser does not support the video tag. Watch Synergy Sound perform live at{" "}
                  {item.venue}.
                </video>

                {/* FIX 3: Overlay is pointer-events-none when playing so it doesn't block native controls */}
                {playingId !== item.id && <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/40 backdrop-blur-[2px] transition-opacity duration-500 cursor-pointer" onClick={() => handlePlayVideo(item.id)}>
                    <Button variant="outline" size="icon" className="w-16 h-16 rounded-lg border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 pointer-events-none" aria-label={`Play video of Synergy Sound at ${item.venue}`}>
                      <Play className="w-8 h-8 fill-current" />
                    </Button>
                  </div>}

                {/* Grain overlay */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none z-10 mix-blend-screen" style={{
              backgroundColor: "hsla(35, 80%, 50%, 0.25)",
              backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22 opacity=%220.3%22/%3E%3C/svg%3E")'
            }} />

                {/* Gradient overlay */}
                <div className={`${item.srcClassName || ""} absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-80 z-[15] group-hover:opacity-60 transition-opacity duration-500 pointer-events-none`} />
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}